// =========================================================
// src/services/shopify.js
// =========================================================
//
// Connects to Shopify Storefront API, fetches products,
// handles pagination, and converts data for Inventory.js
// =========================================================

const STORE_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const STOREFRONT_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;

const SHOPIFY_ENDPOINT = `https://${STORE_DOMAIN}/api/2024-10/graphql.json`;

const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $after: String) {
    products(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          title
          description
          vendor
          variants(first: 250) {
            nodes {
              id
              barcode
              title
              quantityAvailable
            }
          }
          featuredImage {
            url
            altText
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`;

// -----------------------------------------------------------------
// Shopify ke description field mein extra data packed hoti hai is format mein
// -----------------------------------------------------------------
function parsePackedDescription(rawDescription) {
  if (!rawDescription) {
    return { cleanDescription: "", fields: {} };
  }

  const parts = rawDescription.split("$$$$$$$$$$");
  const cleanDescription = (parts[0] || "").trim();
  const fields = {};

  for (let i = 1; i < parts.length; i++) {
    const separatorIndex = parts[i].indexOf("___");
    if (separatorIndex === -1) continue;

    const key = parts[i].slice(0, separatorIndex).trim();
    const value = parts[i].slice(separatorIndex + 3).trim();

    if (key) {
      fields[key] = value;
    }
  }

  return { cleanDescription, fields };
}

// Fetch a single page of products
async function fetchProductsPage(after = null) {
  const response = await fetch(SHOPIFY_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query: PRODUCTS_QUERY,
      variables: { first: 250, after },
    }),
  });

  if (!response.ok) {
    throw new Error(`Shopify API error: ${response.status}`);
  }

  const json = await response.json();

  if (json.errors) {
    throw new Error(json.errors.map((error) => error.message).join(", "));
  }

  if (!json.data || !json.data.products) {
    throw new Error("Shopify returned invalid product data.");
  }

  return json.data.products;
}

// Fetch all products handling pagination
export async function fetchProducts() {
  if (!STORE_DOMAIN) {
    throw new Error("Shopify store domain is missing. Check REACT_APP_SHOPIFY_STORE_DOMAIN.");
  }
  if (!STOREFRONT_TOKEN) {
    throw new Error("Shopify Storefront token is missing. Check REACT_APP_SHOPIFY_STOREFRONT_TOKEN.");
  }

  let allProducts = [];
  let cursor = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const page = await fetchProductsPage(cursor);
    allProducts = allProducts.concat(page.edges);
    hasNextPage = page.pageInfo.hasNextPage;

    if (page.edges.length > 0) {
      cursor = page.edges[page.edges.length - 1].cursor;
    } else {
      cursor = null;
      hasNextPage = false;
    }
  }

  const convertedProducts = allProducts.map(({ node }) => {
    const barcode =
      node?.variants?.nodes?.find((variant) => variant?.barcode)?.barcode || "";

    // Saare variants ki available quantity jodkar total quantity nikalte hain
    const totalQuantity =
      node?.variants?.nodes?.reduce(
        (sum, variant) => sum + (variant?.quantityAvailable || 0),
        0,
      ) || 0;

    // Description field ke andar packed extra data (listOnWebsite, brand, platform, etc.) nikalna
    const { cleanDescription, fields } = parsePackedDescription(node?.description);

    const listOnWebsite =
      (fields.listOnWebsite || "").trim().toLowerCase() === "yes";

    const price =
      node?.priceRange?.minVariantPrice?.amount || "0";

    const currency =
      node?.priceRange?.minVariantPrice?.currencyCode || "USD";

    return {
      id: node?.id || "",
      title: node?.title || "",
      description: cleanDescription,
      vendor: node?.vendor || "",
      barcode,
      image: node?.featuredImage?.url || null,
      imageAlt: node?.featuredImage?.altText || node?.title || "Product",
      price,
      currency,
      // Packed description se nikale gaye extra fields
      brand: fields.brand || node?.vendor || "",
      platform: fields.platform || "",
      rating: fields.ageRating || "",
      productType: fields.productType || "",
      category: fields.category || "",
      condition: fields.condition || "",
      // Filtration ke liye chahiye wale fields
      quantity: totalQuantity,
      listOnWebsite,
      // Raw parsed object bhi rakh rahe hain agar aage kisi aur field ki zaroorat pade
      rawFields: fields,
    };
  });

  return convertedProducts;
}